import type { RefObject } from "react";
import type { RowData, Table } from "@tanstack/react-table";
import {
    type DataGridFeatures,
    type DataGridHandle,
    toggleRowSelection,
} from "@design-system/DataGrid";
import { hasOpenMenu } from "@design-system/Menu/hasOpenMenu";
import { hasOpenPopover } from "@design-system/Menu/hasOpenPopover";
import { useHasOpenModal } from "@design-system/overlay/openModalLock";
import { useKeyboardShortcutOwner } from "@design-system/ViewTable/core/useKeyboardShortcutOwner";
import { useKeyboardCommand } from "@hooks/keyboard/useKeyboardCommand";

const isInsideGrid = (target: EventTarget | null) =>
    target instanceof Element &&
    target.closest("[role='grid'], [role='treegrid']") !== null;

/**
 * The task list's page-wide keys: j/k and ArrowDown/ArrowUp move row focus,
 * Mod+ArrowDown/ArrowUp jump to the last or first row, x toggles the focused
 * row, Shift+A toggles every row, Shift+ArrowDown/ArrowUp start a run with
 * the focused row and the next one. They work with focus anywhere on the page, but not in inputs
 * or while a menu, popover, or modal is open. Inside the grid, the grid's own
 * arrow keys apply instead. Call `activate` when the pointer or focus enters
 * the list, so it owns them.
 */
export const useTaskListShortcuts = <TData extends RowData>({
    gridRef,
    table,
    isEnabled,
}: {
    gridRef: RefObject<DataGridHandle | null>;
    table: Table<DataGridFeatures, TData>;
    isEnabled: boolean;
}) => {
    const {
        isKeyboardShortcutsEnabledForTable,
        activateKeyboardShortcutsForTable,
    } = useKeyboardShortcutOwner({
        tableId: "tasks-list",
        enableKeyboardShortcuts: isEnabled,
    });
    const hasOpenModal = useHasOpenModal();
    const isBlocked = () => hasOpenMenu() || hasOpenPopover() || hasOpenModal;
    const toOptions = (description: string) => ({
        description,
        enabled: isKeyboardShortcutsEnabledForTable,
        ignoreEventWhen: isBlocked,
        preventDefault: false,
    });
    const toOutsideGridOptions = (description: string) => ({
        ...toOptions(description),
        ignoreEventWhen: (event: KeyboardEvent) =>
            isBlocked() || isInsideGrid(event.target),
        preventDefault: true,
    });

    const getFocusedRow = () => {
        const rowId = gridRef.current?.getFocusedRowId();
        return rowId ? table.getRowModel().rowsById[rowId] : undefined;
    };
    const selectFocusedRow = () => {
        const row = getFocusedRow();
        if (row?.getCanSelect()) {
            row.toggleSelected(true);
        }
    };
    /** Starts a run as the grid's own Shift+Arrow does: the row it lands on is selected too. */
    const selectAndMove = (target: "next" | "previous") => {
        selectFocusedRow();
        gridRef.current?.focusRow(target);
        selectFocusedRow();
    };

    useKeyboardCommand({
        keys: "j",
        callback: () => gridRef.current?.focusRow("next"),
        options: toOptions("Go to next row"),
    });
    useKeyboardCommand({
        keys: "k",
        callback: () => gridRef.current?.focusRow("previous"),
        options: toOptions("Go to previous row"),
    });
    useKeyboardCommand({
        keys: "ArrowDown",
        callback: () => gridRef.current?.focusRow("next"),
        options: toOutsideGridOptions("Go to next row"),
    });
    useKeyboardCommand({
        keys: "ArrowUp",
        callback: () => gridRef.current?.focusRow("previous"),
        options: toOutsideGridOptions("Go to previous row"),
    });
    useKeyboardCommand({
        keys: "mod+ArrowDown",
        callback: () => gridRef.current?.focusRow("last"),
        options: { ...toOptions("Jump to the last row"), preventDefault: true },
    });
    useKeyboardCommand({
        keys: "mod+ArrowUp",
        callback: () => gridRef.current?.focusRow("first"),
        options: {
            ...toOptions("Jump to the first row"),
            preventDefault: true,
        },
    });
    useKeyboardCommand({
        keys: "shift+ArrowDown",
        callback: () => selectAndMove("next"),
        options: toOutsideGridOptions(
            "Select current row and go to the next row",
        ),
    });
    useKeyboardCommand({
        keys: "shift+ArrowUp",
        callback: () => selectAndMove("previous"),
        options: toOutsideGridOptions(
            "Select current row and go to the previous row",
        ),
    });
    useKeyboardCommand({
        keys: "x",
        callback: () => {
            const row = getFocusedRow();
            if (row) {
                toggleRowSelection({ row, isRangeEvent: false });
            }
        },
        options: toOptions("Select or deselect row"),
    });
    useKeyboardCommand({
        keys: "shift+a",
        callback: () =>
            table.toggleAllRowsSelected(!table.getIsAllRowsSelected()),
        options: toOptions("Select or deselect all rows"),
    });

    return { activate: activateKeyboardShortcutsForTable };
};
